import React, { Component } from "react";
import { connect } from "react-redux";
import { BrowserRouter, Switch, Route, Redirect } from "react-router-dom";
import axios from "axios";
import _ from "lodash";
import { Helmet } from "react-helmet";
import store from "../store";
import { Translate, I18n } from "react-redux-i18n";

import Toast from "react-bootstrap/Toast";
import Container from "react-bootstrap/Container";

import Router from "./Router.tsx";

import { logout } from "../actions/accountActions";
import { setAlertVisibility, setAlert } from "../actions/alertActions";
import {
  setLanguage,
  fetchAllowedLanguagesOnly,
} from "../actions/languageActions";
import { FALLBACK_LANGUAGE } from "../styles/utils/constants";

import "../styles/main.scss";
import evio_o_black from "../styles/images/evio_o_black.svg";
import * as Sentry from "@sentry/react";

const baseUrl = "/:locale([a-z]{2})?";
const ALERT_TIMEOUT = 5000;

if (
  process.env.REACT_APP_BUILD_MODE === "production" ||
  process.env.REACT_APP_BUILD_MODE === "pre-production"
) {
  if (process.env.REACT_APP_BUILD_MODE === "pre-production") {
    Sentry.init({
      dsn: "https://b26427f496982ba10c7415a522b54961@o4505861147131904.ingest.sentry.io/4505861164171264",
      environment: "pre-production",
      integrations: [
        new Sentry.BrowserTracing({
          // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
          tracePropagationTargets: [
            "localhost",
            "https://webclient.go-evio.com/",
            /^https:\/\/yourserver\.io\/api/,
          ],
        }),
        new Sentry.Replay(),
      ],
      // Performance Monitoring
      tracesSampleRate: 0.2, // Capture 100% of the transactions, reduce in production!
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
  } else {
    Sentry.init({
      dsn: "https://b26427f496982ba10c7415a522b54961@o4505861147131904.ingest.sentry.io/4505861164171264",
      environment: "production",
      integrations: [
        new Sentry.BrowserTracing({
          // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
          tracePropagationTargets: [
            "localhost",
            "https://webclient.go-evio.com/",
            /^https:\/\/yourserver\.io\/api/,
          ],
        }),
        new Sentry.Replay(),
      ],
      // Performance Monitoring
      tracesSampleRate: 0.2, // Capture 100% of the transactions, reduce in production!
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
  }
}

class App extends Component {
  constructor(props) {
    super(props);

    if (
      this.props.token !== axios.defaults.headers.common["token"] ||
      this.props.refreshtoken !== axios.defaults.headers.common["refreshtoken"]
    ) {
      axios.defaults.headers.common["token"] = this.props.token;
      axios.defaults.headers.common["refreshtoken"] = this.props.refreshtoken;
    }
  }

  componentDidMount() {
    axios.interceptors.response.use(
      (response) => response,
      (err) => {
        if (axios.isCancel(err)) {
          return Promise.resolve(err);
        } else {
          if (err && err.response && err.response.data) {
            if (err.response.data.code) {
              if (err.response.data.code === "server_invalid_credentials") {
                this.props.setAlert(I18n.t("invalid_credentials"), "danger");
              }
              if (err.response.data.code === "server_user_not_valid") {
                if (
                  !this.props.invalidLogin &&
                  !_.isEmpty(this.props.accountData)
                ) {
                  this.props.logout(true);
                  this.props.setAlert(
                    I18n.t(err.response.data.message),
                    "danger"
                  );
                }
              } else if (
                err.response.data.code === "server_license_plate_exists"
              ) {
                this.props.setAlert(I18n.t("plate_already_exists"), "danger");
              } else if (err.response.data.code === "server_user_not_active") {
                this.props.logout();
                window.location.assign("../verifyEditedAccountCode");
                this.props.setAlert(
                  I18n.t(err.response.data.message),
                  "danger"
                );
              } else if (
                !(err.response.data.code === "server_users_not_found") &&
                err.response.config.url === "/api/private/users" &&
                /\bhttps:\/\/[^?]+\?[^&]*\bmobile\b[^&]*\binternationalPrefix\b/.test(
                  err.response.request.responseURL
                )
              ) {
                this.props.setAlert(
                  I18n.t(err.response.data.message),
                  "danger"
                );
              }
            } else if (err.response.data.code) {
              this.props.setAlert(err.response.data.message, "danger");
            } else this.props.setAlert(I18n.t("general_error"), "danger");
          }
          return Promise.reject(err);
        }
      }
    );

    this.updateTabTitleAndIcon();

    store.getState().account.translationNeedsReload &&
      this.props.fetchAllowedLanguagesOnly();
  }

  loadZohoScript() {
    if (document.getElementById("zsiqchat")) return;
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.id = "zsiqchat";
    script.defer = true;
    script.innerHTML = `
      var $zoho=$zoho || {};$zoho.salesiq = $zoho.salesiq || {widgetcode: "siq15eeee86d01642ff714ec7ed40b73ede303ef3aa771d7184644c90f61ae1b090", values:{},ready:function(){}};var d=document;s=d.createElement("script");s.type="text/javascript";s.id="zsiqscript";s.defer=true;s.src="https://salesiq.zohopublic.com/widget";t=d.getElementsByTagName("script")[0];t.parentNode.insertBefore(s,t);
    `;
    document.body.appendChild(script);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps?.token !== this.props.token ||
      prevProps?.refreshtoken !== this.props.refreshtoken
    ) {
      axios.defaults.headers.common["token"] = this.props.token;
      axios.defaults.headers.common["refreshtoken"] = this.props.refreshtoken;
    }
  }

  updateTabTitleAndIcon() {
    const favicon = document.querySelector("link[rel~='icon']");
    favicon.href =
      this.props.brand?.icons?.favicon ??
      process.env.PUBLIC_URL + "/favicon.png";

    if (this.props.brand?.brandName === "Hyundai") {
      document.title = "Portal Blue";
      this.loadZohoScript();
    } else if (this.props.brand?.brandName === "Salvador Caetano") {
      document.title = "Go.Charge Webclient";
    } else {
      document.title = `${this.props.brand?.brandName} Webclient`;
    }

    axios.defaults.headers.common["clientname"] =
      this.props.brand?.brandName || "EVIO";
  }

  render() {
    return (
      <>
        <Container fluid={true} className="body">
          <BrowserRouter>
            <Switch>
              <Route path={baseUrl}>
                {({ match, location }) => {
                  const params = match ? match.params : {};

                  let { locale = this.props.locale || FALLBACK_LANGUAGE } =
                    params;

                  // validate if languages have been loaded.
                  if (
                    !this.props.languages ||
                    this.props.languages.length === 0
                  )
                    return;

                  // check if locale selected is supported by EVIO
                  // if not, default to english
                  if (!this.props.languages.includes(locale)) {
                    locale = FALLBACK_LANGUAGE;
                  }
                  const { pathname } = location;
                  /*
                  if (!pathname.includes(`/${locale}`)) {
                    if (pathname === "/")
                      return <Redirect to={`/${locale}/login`} />;
                    else return <Redirect to={`/${locale + pathname}`} />;
                  }
                  */

                  return <Router locale={locale} pathname={pathname} />;
                }}
              </Route>
            </Switch>
          </BrowserRouter>
        </Container>

        <Helmet>
          {/* Google Analytics */}
          <script
            async
            src="https://www.googletagmanager.com/gtag/js?id=G-PT4DN9VKJZ"
          ></script>
          <script>
            {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-PT4DN9VKJZ');
          `}
          </script>
        </Helmet>

        <Toast
          id="mainAlert"
          className={this.props.alertVariant}
          show={this.props.alertVisible}
          onClose={() => this.props.setAlertVisibility(false)}
          delay={ALERT_TIMEOUT}
          autohide
        >
          <Toast.Header>
            <img src={evio_o_black} alt="" />
            <Translate
              value={`general_${
                this.props.alertVariant === "danger" ? "error" : "success"
              }`}
            />
          </Toast.Header>
          <Toast.Body>
            {typeof this.props.alertContent === "string" ? (
              this.props.alertContent
            ) : (
              <>
                <h6>{this.props?.alertContent?.title}</h6>
                <p>{this.props?.alertContent?.body}</p>
              </>
            )}
          </Toast.Body>
        </Toast>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  locale: state.i18n.locale,
  languages: state.language.languages,
  translation: state.language.translation,
  brand: state.app.brand,
  accountData: state.account.accountData,
  token: state.account.token,
  refreshtoken: state.account.refreshtoken,
  loggedIn: state.account.loggedIn,
  redirect: state.account.redirect,
  changedEmail: state.account.changedEmail,
  alertVisible: state.alert.alertVisible,
  alertContent: state.alert.alertContent,
  alertVariant: state.alert.alertVariant,
  alertClose: state.alert.alertClose,
});

export default connect(mapStateToProps, {
  logout,
  setAlert,
  setAlertVisibility,
  setLanguage,
  fetchAllowedLanguagesOnly,
})(App);
